import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { useForm } from 'react-hook-form';
import { CreditCard, ArrowUpCircle, ArrowDownCircle } from 'lucide-react';
import toast from 'react-hot-toast';

const AddTransaction = () => {
  const { dispatch } = useApp();
  const navigate = useNavigate();
  const { register, handleSubmit, watch, formState: { errors }, reset } = useForm();

  const transactionType = watch('transactionType');

  const onSubmit = (data) => {
    const transactionData = {
      ...data,
      amount: parseFloat(data.amount),
      commission: parseFloat(data.commission || 0),
      createdAt: new Date().toISOString()
    };

    dispatch({ type: 'ADD_TRANSACTION', payload: transactionData });
    toast.success('Transaction added successfully!');
    reset();
    navigate('/transactions');
  };

  return (
    <div className="p-8">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <CreditCard className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl font-bold gradient-text mb-2">Add Transaction</h1>
          <p className="text-gray-600 text-lg">Record a new financial transaction</p>
        </div>

        <div className="card">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  {...register('fullName', { required: 'Full name is required' })}
                  className="input-field"
                  placeholder="Enter full name"
                />
                {errors.fullName && (
                  <p className="text-red-500 text-sm mt-1">{errors.fullName.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mobile Number *
                </label>
                <input
                  type="tel"
                  {...register('mobileNumber', { 
                    required: 'Mobile number is required',
                    pattern: {
                      value: /^[0-9]{10}$/,
                      message: 'Please enter a valid 10-digit mobile number'
                    }
                  })}
                  className="input-field"
                  placeholder="Enter mobile number"
                />
                {errors.mobileNumber && (
                  <p className="text-red-500 text-sm mt-1">{errors.mobileNumber.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Aadhaar Last 4 Digits *
                </label>
                <input
                  type="text"
                  {...register('aadhaarLast4', { 
                    required: 'Aadhaar last 4 digits are required',
                    pattern: {
                      value: /^[0-9]{4}$/,
                      message: 'Please enter exactly 4 digits'
                    }
                  })}
                  className="input-field"
                  placeholder="Enter last 4 digits"
                  maxLength="4"
                />
                {errors.aadhaarLast4 && (
                  <p className="text-red-500 text-sm mt-1">{errors.aadhaarLast4.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Transaction Type *
                </label>
                <select
                  {...register('transactionType', { required: 'Transaction type is required' })}
                  className="input-field"
                >
                  <option value="">Select transaction type</option>
                  <option value="Deposit">Deposit</option>
                  <option value="Withdraw">Withdraw</option>
                </select>
                {errors.transactionType && (
                  <p className="text-red-500 text-sm mt-1">{errors.transactionType.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Amount *
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">₹</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    {...register('amount', { 
                      required: 'Amount is required',
                      min: { value: 0.01, message: 'Amount must be greater than 0' }
                    })}
                    className="input-field pl-8"
                    placeholder="Enter amount"
                  />
                </div>
                {errors.amount && (
                  <p className="text-red-500 text-sm mt-1">{errors.amount.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Commission
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">₹</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    {...register('commission')}
                    className="input-field pl-8"
                    placeholder="Enter commission (optional)"
                  />
                </div>
              </div>
            </div>

            {/* Transaction Type Indicator */}
            {transactionType && (
              <div className="flex items-center justify-center p-4 rounded-xl bg-gradient-to-r from-gray-50 to-gray-100">
                <div className={`flex items-center space-x-3 px-6 py-3 rounded-xl ${
                  transactionType === 'Deposit' 
                    ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white' 
                    : 'bg-gradient-to-r from-red-500 to-pink-500 text-white'
                }`}>
                  {transactionType === 'Deposit' ? (
                    <ArrowUpCircle className="w-6 h-6" />
                  ) : (
                    <ArrowDownCircle className="w-6 h-6" />
                  )}
                  <span className="font-semibold text-lg">{transactionType} Transaction</span>
                </div>
              </div>
            )}

            <div className="flex space-x-4 pt-6">
              <button
                type="submit"
                className="btn-primary flex-1"
              >
                <CreditCard className="w-5 h-5 mr-2" />
                Save Transaction
              </button>
              <button
                type="button"
                onClick={() => navigate('/transactions')}
                className="btn-secondary"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddTransaction;
